import React, { useEffect, useState } from "react";
import { X, Users, Search, Check } from "lucide-react";
import { toast } from "react-toastify";
import requests from "../../services/httpServices";

const ShareElementModal = ({ isOpen, onClose, element, onSaved }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await requests.get("/element/team-members");
        const list = res?.members || [];
        setMembers(list);
        // Pre-check already-shared members
        const shared = new Set((element?.sharedWith || []).map(String));
        setSelected(shared);
      } catch (err) {
        console.error("Failed to load team members:", err);
        toast.error("Failed to load team members");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, element]);

  const toggle = (userId) => {
    if (!userId) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(String(userId))) next.delete(String(userId));
      else next.add(String(userId));
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sharedWith = Array.from(selected);
      await requests.put(`/element/${element._id || element.id}/share`, {
        sharedWith,
      });
      toast.success("Sharing updated");
      onSaved?.(sharedWith);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update sharing");
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers = members.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#001233] rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 relative border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Share with Team
            </h3>
            <p className="text-xs text-gray-400">
              {element?.name || "Element"}
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-400 mb-4 mt-2">
          Select team members who can use this element in their rooms.
        </p>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] text-sm"
          />
        </div>

        {/* Members list */}
        <div className="max-h-64 overflow-y-auto space-y-1 mb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              {members.length === 0
                ? "No team members yet. Invite members from Team / Users."
                : "No matching members."}
            </div>
          ) : (
            filteredMembers.map((m) => {
              const hasUserId = !!m.userId;
              const isSelected = hasUserId && selected.has(String(m.userId));
              return (
                <button
                  key={m.email}
                  onClick={() => toggle(m.userId)}
                  disabled={!hasUserId}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                    !hasUserId
                      ? "opacity-50 cursor-not-allowed"
                      : isSelected
                      ? "bg-[#6a1fbf]/20 hover:bg-[#6a1fbf]/30"
                      : "hover:bg-white/5"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? "bg-[#6a1fbf] border-[#6a1fbf]"
                        : "border-white/20"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {m.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{m.email}</p>
                  </div>
                  {m.status !== "accepted" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 flex-shrink-0">
                      Pending
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareElementModal;
